import React from "react";
import SuporteIcon from "../empreenderUI/ui/appsIcons/SuporteIcon";
import { EmpreenderApps } from "../files/AppsArray";
import { useTranslation } from "react-i18next";
import { usePergunta } from "../contexts/PerguntaContext";
import { formatarResposta } from "../lib/formatarResposta";
import BarraResolveu from "./BarraResolveu";
import CartaoEncaminhamento from "./CartaoEncaminhamento";
import VideoArtigo from "./VideoArtigo";
import ArtigoUsado from "./ArtigoUsado";
import { primeiroVideo } from "../lib/video";
import { useEnviarMensagem } from "../hooks/EnviarMensagem";
import type { OrigemAtendente } from "../lib/metricas";

interface Artigo {
  nomeartigo: string;
  link: string;
  descricao?: string;
  video?: string;
}

interface WhatsApp {
  img: string;
  nome: string;
  ultimostatus: string;
  whatsapp: string;
}

interface MensagemProps {
  id: string;
  texto: string;
  tipo: "usuario" | "assistente";
  artigos: Artigo[];
  whatsapps: WhatsApp[];
  slugAtual: string;
  idRespostaGptSak?: number | null;
  feedback?: "sim" | "nao";
  // Texto lido do print que a pessoa anexou (só em mensagens do usuário).
  anexoTexto?: string;
  // Da IA da central: respostas prontas clicáveis, link do artigo usado e se sugere atendente.
  opcoes?: string[];
  artigoUsado?: string | null;
  precisaHumano?: boolean;
  fonte?: "ia";
  artigoUsadoTitulo?: string | null;
  artigoHtml?: string | null;
  // Número da resposta nas métricas e de onde vem o botão do atendente na primeira linha.
  registro?: number | null;
  origemAtendente?: OrigemAtendente;
  // Só a última resposta da conversa pergunta "Isso resolveu?".
  ultima?: boolean;
  perguntaOriginal?: string;
}

const decodificarHtml = (texto: string) =>
  new DOMParser().parseFromString(texto, "text/html").documentElement.textContent || "";

const Mensagem: React.FC<MensagemProps> = ({
  id,
  texto,
  tipo,
  artigos,
  whatsapps,
  slugAtual,
  idRespostaGptSak,
  feedback,
  anexoTexto,
  opcoes,
  artigoUsado,
  precisaHumano,
  fonte,
  artigoUsadoTitulo,
  artigoHtml,
  registro = null,
  origemAtendente = "sugerido",
  ultima = false,
  perguntaOriginal = "",
}) => {
  const { t } = useTranslation();
  const { appName, pergunta } = usePergunta();
  const enviarMensagem = useEnviarMensagem(slugAtual);

  const dadosApp = EmpreenderApps.find(
    (item) => item.slug === slugAtual || item.sluges === slugAtual,
  );

  if (tipo !== "assistente") {
    return (
      <div id={`mensagem-${id}`} className="flex flex-col items-end p-0 pb-3 m-0 usuario">
        <p className="m-0">{texto || t("mensagem_nao_disponivel")}</p>
        {anexoTexto !== undefined && <span className="selo-print">{t("print_anexado")}</span>}
      </div>
    );
  }

  const pediuAjuda = texto === "pediu_ajuda";
  const video = pediuAjuda ? null : primeiroVideo(texto, artigos, artigoUsado);

  return (
    <div id={`mensagem-${id}`} className="flex p-0 pb-3 m-0 w-full assistente">
      <div className="resposta-gpt flex w-full">
        <div className="icone-assistente">{dadosApp?.icon || <SuporteIcon />}</div>
        <div className="flex-1 min-w-0">
          {pediuAjuda ? (
            <CartaoEncaminhamento whatsapps={whatsapps || []} slugAtual={slugAtual} />
          ) : (
            <>
              <p className="autor-resposta">{appName}</p>
              <div
                className="resposta"
                dangerouslySetInnerHTML={{
                  __html: formatarResposta(texto || t("mensagem_nao_disponivel")),
                }}
              />

              {ultima && opcoes && opcoes.length > 0 && (
                <div className="opcoes-ia">
                  {opcoes.map((opcao) => (
                    <button
                      key={opcao}
                      className="opcao-ia"
                      disabled={pergunta !== ""}
                      onClick={() => enviarMensagem(opcao)}
                    >
                      {opcao}
                    </button>
                  ))}
                </div>
              )}

              {video && <VideoArtigo video={video} />}

              {/* Resposta da IA: só o artigo em que ela se baseou, com o artigo completo dentro do
                  chat. Respostas antigas e do plano B (SAK direto) continuam com os cartões. */}
              {fonte === "ia" && artigoUsado && (
                <ArtigoUsado
                  titulo={artigoUsadoTitulo || artigos?.[0]?.nomeartigo || ""}
                  link={artigoUsado}
                  html={artigoHtml || undefined}
                />
              )}

              {fonte !== "ia" && artigos?.length > 0 && (
                <div className="artigos-relacionados">
                  <p className="artigos-titulo">{t("artigos_relacionados")}</p>
                  <div className="artigos-cartoes">
                    {artigos.map((artigo) => (
                      <a
                        key={artigo.link}
                        href={artigo.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="artigo-cartao"
                      >
                        <span className="artigo-cartao-titulo">
                          {decodificarHtml(artigo.nomeartigo)}
                        </span>
                        {artigo.descricao && (
                          <span className="artigo-cartao-descricao">
                            {decodificarHtml(artigo.descricao)}
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {ultima && (
                <BarraResolveu
                  mensagemId={id}
                  slugAtual={slugAtual}
                  idRespostaGptSak={idRespostaGptSak}
                  feedback={feedback}
                  perguntaOriginal={perguntaOriginal}
                  sugerirHumano={precisaHumano}
                  registro={registro}
                  origemAtendente={origemAtendente}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Mensagem;
